(() => {
  const initialized = new WeakSet();
  const routeRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  const configuredAssemblyVariantId = Number(window.AssemblyServiceVariantId || 42786032386123);
  const syncState = {
    running: false,
    queued: false,
    lastSignature: ''
  };

  function isCartPage() {
    return window.location.pathname.replace(/\/$/, '') === `${routeRoot}cart`.replace(/\/$/, '');
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  function moneyTextToCents(text) {
    const number = Number(String(text || '').replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(number)) {
      return null;
    }
    return Math.round(number * 100);
  }

  async function getCart() {
    const response = await fetch(`${routeRoot}cart.js`, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Could not fetch cart');
    }

    return response.json();
  }

  function isAssemblyServiceItem(item) {
    return Number(item.variant_id) === configuredAssemblyVariantId;
  }

  function isAssemblyEligibleItem(item) {
    if (isAssemblyServiceItem(item)) {
      return false;
    }

    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    const productType = String(item.product_type || '').toLowerCase();
    const title = String(item.product_title || item.title || '').toLowerCase();
    const sku = String(item.sku || '').toLowerCase();
    const combined = `${handle} ${productType} ${title} ${sku}`;

    const excludedTerms = [
      'assembly service',
      'sample',
      'door sample',
      'sample door',
      'trim',
      'molding',
      'moulding',
      'filler',
      'panel',
      'skin',
      'toe kick',
      'toe-kick',
      'hardware',
      'knob',
      'pull',
      'hinge',
      'shelf',
      'shelves',
      'accessory',
      'accessories',
      'floating shelf',
      'floating shelves',
      'light rail',
      'crown',
      'valance'
    ];

    if (excludedTerms.some((term) => combined.includes(term))) {
      return false;
    }

    const eligibleTerms = [
      'assembly-eligible',
      'base cabinet',
      'base cabinets',
      'wall cabinet',
      'wall cabinets',
      'upper cabinet',
      'upper cabinets',
      'tall cabinet',
      'tall cabinets',
      'pantry cabinet',
      'pantry cabinets',
      'vanity cabinet',
      'vanity cabinets',
      'sink base',
      'drawer base',
      'oven cabinet',
      'microwave cabinet',
      'lazy susan cabinet',
      'blind corner cabinet'
    ];

    return eligibleTerms.some((term) => combined.includes(term));
  }

  function getEligibleQuantity(cart) {
    return cart.items.reduce((sum, item) => {
      if (!isAssemblyEligibleItem(item)) {
        return sum;
      }

      return sum + Number(item.quantity || 0);
    }, 0);
  }

  function getAssemblyQuantity(cart) {
    const item = cart.items.find(isAssemblyServiceItem);
    return item ? Number(item.quantity || 0) : 0;
  }

  function isAssemblyEnabled(cart) {
    return getAssemblyQuantity(cart) > 0 || cart.attributes?.['Assembly Service'] === 'Yes';
  }

  function updateToggleViews(cart, message = '') {
    const eligibleQuantity = getEligibleQuantity(cart);
    const assemblyQuantity = getAssemblyQuantity(cart);
    const enabled = assemblyQuantity > 0;

    document.querySelectorAll('[data-assembly-service]').forEach((root) => {
      const switchButton = root.querySelector('[data-assembly-switch]');
      const status = root.querySelector('[data-assembly-status]');
      const help = root.querySelector('[data-assembly-help]');
      const fee = root.querySelector('[data-assembly-fee]');
      const feeTotal = root.querySelector('[data-assembly-fee-total]');
      const unitPrice = Number(root.dataset.assemblyUnitPrice || 7500);

      root.dataset.eligibleQuantity = String(eligibleQuantity);

      if (switchButton) {
        switchButton.disabled = eligibleQuantity === 0;
        switchButton.setAttribute('aria-checked', enabled ? 'true' : 'false');
      }

      if (help) {
        help.textContent = eligibleQuantity > 0
          ? 'Add assembly service to eligible cabinets.'
          : 'Add an eligible cabinet to use assembly service.';
      }

      if (fee) {
        fee.hidden = !enabled;
      }

      if (feeTotal) {
        feeTotal.textContent = formatMoney(assemblyQuantity * unitPrice);
      }

      if (status && message) {
        status.textContent = message;
      }
    });
  }

  function cartSignature(cart) {
    return cart.items
      .map((item) => `${item.variant_id}:${item.quantity}`)
      .sort()
      .join('|');
  }

  async function setAssemblyQuantity(quantity, enabled) {
    const updates = {};
    updates[configuredAssemblyVariantId] = quantity;

    const response = await fetch(`${routeRoot}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates,
        attributes: {
          'Assembly Service': enabled && quantity > 0 ? 'Yes' : 'No'
        }
      })
    });

    if (!response.ok) {
      throw new Error('Assembly service sync failed');
    }

    return response.json();
  }

  async function syncAssemblyService(reason = 'cart changed') {
    if (!configuredAssemblyVariantId) {
      return;
    }

    if (syncState.running) {
      syncState.queued = true;
      return;
    }

    syncState.running = true;

    try {
      const cart = await getCart();
      const signature = cartSignature(cart);
      const enabled = isAssemblyEnabled(cart);
      const eligibleQuantity = getEligibleQuantity(cart);
      const assemblyQuantity = getAssemblyQuantity(cart);
      let finalCart = cart;

      if (enabled && eligibleQuantity !== assemblyQuantity) {
        finalCart = await setAssemblyQuantity(eligibleQuantity, eligibleQuantity > 0);
      }

      if (!enabled && assemblyQuantity > 0) {
        finalCart = await setAssemblyQuantity(0, false);
      }

      syncState.lastSignature = cartSignature(finalCart) || signature;
      updateVisibleTotals(finalCart);
      updateToggleViews(finalCart, reason === 'toggle' ? '' : 'Assembly service updated.');
      document.dispatchEvent(new CustomEvent('assembly:updated', { detail: { cart: finalCart } }));
    } catch (error) {
      // Keep the cart usable if Shopify rejects a background sync.
    } finally {
      syncState.running = false;

      if (syncState.queued) {
        syncState.queued = false;
        window.setTimeout(() => syncAssemblyService(reason), 120);
      }
    }
  }

  function scheduleAssemblySync(reason) {
    window.clearTimeout(scheduleAssemblySync.timer);
    scheduleAssemblySync.timer = window.setTimeout(() => syncAssemblyService(reason), 250);
  }

  async function removeAssemblyServiceAwayFromCart() {
    if (isCartPage() || !configuredAssemblyVariantId) {
      return;
    }

    try {
      const cart = await getCart();
      const hasAssemblyService = cart.items.some((item) => item.variant_id === configuredAssemblyVariantId);

      if (!hasAssemblyService) {
        return;
      }

      const updates = {};
      updates[configuredAssemblyVariantId] = 0;

      const response = await fetch(`${routeRoot}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          updates,
          attributes: {
            'Assembly Service': 'No'
          }
        })
      });

      if (!response.ok) {
        return;
      }

      const updatedCart = await response.json();
      updateVisibleTotals(updatedCart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
      document.dispatchEvent(new CustomEvent('ajaxCart:updated', { detail: { cart: updatedCart } }));
    } catch (error) {
      // Keep browsing uninterrupted if Shopify does not return cart JSON.
    }
  }

  function updateVisibleTotals(cart) {
    const formattedTotal = formatMoney(cart.total_price);
    const formattedItemCount = cart.item_count;

    document.querySelectorAll('[data-cart-total], [data-cart-subtotal], .cart-subtotal, .cart-drawer__subtotal, [data-section-ref="totals"] [data-money]').forEach((node) => {
      node.textContent = formattedTotal;
    });

    document.querySelectorAll('[data-cart-count], .cart-count, .cart-item-count').forEach((node) => {
      const text = node.textContent || '';
      if (text.match(/\d/)) {
        node.textContent = text.replace(/\d+/, formattedItemCount);
      }
    });
  }

  class AssemblyServiceToggle {
    constructor(root) {
      if (initialized.has(root)) {
        return;
      }

      initialized.add(root);

      this.root = root;
      this.switchButton = root.querySelector('[data-assembly-switch]');
      this.status = root.querySelector('[data-assembly-status]');
      this.fee = root.querySelector('[data-assembly-fee]');
      this.feeTotal = root.querySelector('[data-assembly-fee-total]');
      this.variantId = Number(root.dataset.assemblyVariantId);
      this.eligibleQuantity = Number(root.dataset.eligibleQuantity || 0);
      this.unitPrice = Number(root.dataset.assemblyUnitPrice || 7500);

      if (!this.switchButton || !this.variantId) {
        return;
      }

      this.switchButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      });
    }

    async toggle() {
      const currentlyEnabled = this.switchButton.getAttribute('aria-checked') === 'true';
      const currentCart = await getCart();
      const currentEligibleQuantity = getEligibleQuantity(currentCart);
      const nextQuantity = currentlyEnabled ? 0 : currentEligibleQuantity;

      if (currentEligibleQuantity < 1 && !currentlyEnabled) {
        this.setStatus('Add an eligible cabinet first.');
        return;
      }

      this.eligibleQuantity = currentEligibleQuantity;
      this.root.dataset.eligibleQuantity = String(currentEligibleQuantity);
      this.switchButton.disabled = true;
      this.setStatus('Updating assembly service...');

      try {
        const updates = {};
        updates[this.variantId] = nextQuantity;

        const response = await fetch(`${routeRoot}cart/update.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            updates,
            attributes: {
              'Assembly Service': nextQuantity > 0 ? 'Yes' : 'No'
            }
          })
        });

        if (!response.ok) {
          throw new Error('Cart update failed');
        }

        const cart = await response.json();
        this.switchButton.setAttribute('aria-checked', nextQuantity > 0 ? 'true' : 'false');
        this.updateFee(nextQuantity);
        updateVisibleTotals(cart);
        updateToggleViews(cart);
        this.setStatus(nextQuantity > 0 ? 'Assembly service added.' : 'Assembly service removed.');

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('ajaxCart:updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('assembly:updated', { detail: { cart } }));

        if (isCartPage()) {
          window.location.reload();
        }
      } catch (error) {
        this.setStatus('Could not update assembly service. Please try again.');

        try {
          const cart = await getCart();
          updateVisibleTotals(cart);
        } catch (cartError) {
          // Leave the visible cart alone if Shopify does not return cart JSON.
        }
      } finally {
        scheduleAssemblySync('toggle');
      }
    }

    updateFee(quantity) {
      if (!this.fee) {
        return;
      }

      this.fee.hidden = quantity === 0;

      if (this.feeTotal) {
        this.feeTotal.textContent = formatMoney(quantity * this.unitPrice);
      }
    }

    setStatus(message) {
      if (this.status) {
        this.status.textContent = message;
      }
    }
  }

  function initAssemblyServiceToggles() {
    document.querySelectorAll('[data-assembly-service]').forEach((root) => {
      new AssemblyServiceToggle(root);
    });
  }

  document.addEventListener('DOMContentLoaded', initAssemblyServiceToggles);
  document.addEventListener('DOMContentLoaded', removeAssemblyServiceAwayFromCart);
  document.addEventListener('DOMContentLoaded', () => scheduleAssemblySync('cart loaded'));
  document.addEventListener('cart:updated', initAssemblyServiceToggles);
  document.addEventListener('cart:updated', () => scheduleAssemblySync('cart changed'));
  document.addEventListener('ajaxCart:updated', initAssemblyServiceToggles);
  document.addEventListener('ajaxCart:updated', () => scheduleAssemblySync('cart changed'));
  document.addEventListener('cart:build', initAssemblyServiceToggles);
  document.addEventListener('cart:build', () => scheduleAssemblySync('cart changed'));
  document.addEventListener('cart:change', () => scheduleAssemblySync('cart changed'));
  document.addEventListener('cart:refresh', () => scheduleAssemblySync('cart changed'));

  document.addEventListener('change', (event) => {
    if (event.target.matches('input[name="updates[]"], input[name^="updates"], input[name="quantity"], [data-qty-input], quantity-input input')) {
      scheduleAssemblySync('cart quantity changed');
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('cart-remove-button, [href*="/cart/change"], [data-cart-remove], [data-remove], [data-qty-minus], [data-qty-plus], button[name="minus"], button[name="plus"]')) {
      scheduleAssemblySync('cart edited');
    }
  });

  if (!window.__assemblyServiceFetchPatched) {
    window.__assemblyServiceFetchPatched = true;
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      try {
        const request = args[0];
        const url = typeof request === 'string' ? request : request?.url || '';

        if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear')) {
          scheduleAssemblySync('cart ajax changed');
        }
      } catch (error) {
        // Ignore URL parsing issues; the original request already succeeded.
      }

      return response;
    };
  }

  const observer = new MutationObserver(initAssemblyServiceToggles);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
